<script setup>
import {userTimelineDataType} from "~/constants/userTimelineConstants.js";
import FixtureResultRow from "~/components/fixtures/FixtureResultRow.vue";
import StandingsTable from "~/components/leagues/StandingsTable.vue";
import LadderView from "~/components/ladders/LadderView.vue";
import ClubClickableCell from "~/components/lists/ClubClickableCell.vue";
import MediaTimelineItem from "~/components/timeline/MediaTimelineItem.vue";
import TournamentStageTimelineItem from "~/components/timeline/TournamentStageTimelineItem.vue";
import LadderChallengeCell from "~/components/ladders/LadderChallengeCell.vue";
import AvatarImageLoader from "~/components/loaders/AvatarImageLoader.vue";
import {useTimelineModels} from "~/composables/useTimelineModels";

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const {playerName: playerFullName, playerInitials} = useTimelineModels();
// Player-joined cell falls back to "New member" when the name is empty.
const playerName = (p) => playerFullName(p) || 'New member';
</script>

<template>
  <div>
    <template v-if="item.dataType === userTimelineDataType.Fixture">
      <fixture-result-row :fixture="item.fixture"/>
    </template>
    <template v-else-if="item.dataType === userTimelineDataType.LeagueCycleDivision">
      <standings-table v-if="item.leagueCycleDivision?.teams" :division="item.leagueCycleDivision"/>
    </template>
    <template v-else-if="item.dataType === userTimelineDataType.Ladder">
      <ladder-view :simple="true" v-if="item.ladder.teams" :ladder="item.ladder"/>
    </template>
    <template v-else-if="item.dataType === userTimelineDataType.Club">
      <club-clickable-cell :simple="true" :club="item.club"/>
    </template>
    <template v-else-if="item.dataType === userTimelineDataType.TournamentStage">
      <tournament-stage-timeline-item v-if="item.stage" :stage="item.stage"/>
    </template>
    <template v-else-if="item.dataType === userTimelineDataType.Challenge">
      <ladder-challenge-cell v-if="item.challenge" :challenge="item.challenge" :bordered="true"/>
    </template>
    <template v-else-if="item.dataType === userTimelineDataType.Player">
      <!-- join-row (mockups .feed-item player): avatar + name + "New member" -->
      <div v-if="item.player" class="tl-join-row">
        <avatar-image-loader
            v-if="item.player.imageFileName"
            :image-file-name="item.player.imageFileName" :image-clickable="false" size="40" class="tl-join-img"
        />
        <span v-else class="tl-join-face">{{ playerInitials(item.player) }}</span>
        <div class="tl-join-who">
          <div class="tl-join-name">{{ playerName(item.player) }}</div>
          <div class="tl-join-sub">New member</div>
        </div>
      </div>
    </template>
    <template v-else-if="item.dataType === userTimelineDataType.Image || item.dataType === userTimelineDataType.Video">
      <media-timeline-item :item="item"/>
    </template>
    <template v-else-if="item.dataType === userTimelineDataType.Text">
      <div class="text-timeline-item">
        <h4 v-if="item.title">{{ item.title }}</h4>
        <p v-if="item.subTitle" style="white-space: pre-line;">{{ item.subTitle }}</p>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
/* player-joined join-row (mockup .feed-item .join-row) */
.tl-join-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px 14px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  background: rgb(var(--v-theme-bg));
}

.tl-join-img {
  border-radius: 50% !important;
  flex-shrink: 0;
}

.tl-join-face {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;
}

.tl-join-who {
  min-width: 0;
}

.tl-join-name {
  font-size: 13px;
  font-weight: 600;
}

.tl-join-sub {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

/* announcement-style text body per mockups/redesign/home.html club news card */
.text-timeline-item {
  h4 {
    font-size: 13.5px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  p {
    font-size: 12.5px;
    color: rgb(var(--v-theme-charcoal));
    line-height: 1.6;
    margin-top: 4px;
  }
}
</style>
